import { AlertCircle, Check, Loader2, Save } from 'lucide-react';
import React from 'react';

// Local helper: compact relative time string for autosave status lines
const formatDistanceToNow = (date: Date | null): string => {
  if (!date) return '';
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ago`;
};

interface SaveStatusProps {
  isAutoSaving?: boolean;
  lastAutoSaveTime?: Date | null;
  autoSaveError?: string | null;
  hasUnsavedChanges?: boolean;
  errorCount?: number;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'text-xs gap-1',
  md: 'text-sm gap-1.5',
  lg: 'text-base gap-2',
};

const iconSizes = {
  sm: 12,
  md: 14,
  lg: 16,
};

/**
 * SaveStatus
 *
 * Enhanced presentational component that renders the autosave state.
 * Displays error, saving animation, unsaved changes indicator, or relative time since last save.
 * 
 * Visual states:
 * - Error (red): Shows when auto-save fails, includes error count if multiple failures
 * - Saving (animated): Shows during active save operation
 * - Unsaved changes (yellow/orange): Shows when user has made changes that haven't been saved yet
 * - Saved (green/gray): Shows time since last successful save
 */
const SaveStatus: React.FC<SaveStatusProps> = ({
  isAutoSaving,
  lastAutoSaveTime,
  autoSaveError,
  hasUnsavedChanges = false,
  errorCount = 0,
  showIcon = true,
  size = 'sm',
}) => {
  // Error state - highest priority
  if (autoSaveError) {
    return (
      <span 
        className={`inline-flex items-center ${sizeClasses[size]} text-red-600 font-medium`}
        role="alert"
        aria-live="assertive"
      >
        {showIcon && <AlertCircle size={iconSizes[size]} aria-hidden="true" />}
        <span>
          Error saving
          {errorCount > 1 && (
            <span className="ml-1 text-red-500">({errorCount} attempts)</span>
          )}
        </span>
      </span>
    );
  }

  // Actively saving
  if (isAutoSaving) {
    return (
      <span 
        className={`inline-flex items-center ${sizeClasses[size]} text-blue-600 font-medium`}
        aria-live="polite"
      >
        {showIcon && (
          <Loader2 size={iconSizes[size]} className="animate-spin" aria-hidden="true" />
        )}
        <span>Saving...</span>
      </span>
    );
  }

  // Has unsaved changes (debouncing or waiting for next save trigger)
  if (hasUnsavedChanges) {
    return (
      <span 
        className={`inline-flex items-center ${sizeClasses[size]} text-amber-600 font-medium`}
        aria-live="polite"
        title="Changes will be saved automatically"
      >
        {showIcon && <Save size={iconSizes[size]} aria-hidden="true" />}
        <span>Unsaved changes</span>
      </span>
    );
  }

  // Successfully saved
  if (lastAutoSaveTime) {
    return (
      <span 
        className={`inline-flex items-center ${sizeClasses[size]} text-green-600`}
        aria-live="polite"
      >
        {showIcon && <Check size={iconSizes[size]} aria-hidden="true" />}
        <span>Saved {formatDistanceToNow(lastAutoSaveTime)}</span>
      </span>
    );
  }

  // Default state - no saves yet
  return (
    <span 
      className={`inline-flex items-center ${sizeClasses[size]} text-gray-400`}
      aria-live="polite"
    >
      {showIcon && <Save size={iconSizes[size]} aria-hidden="true" />}
      <span>Not saved yet</span>
    </span>
  );
};

export default SaveStatus;
